import React from 'react';
import * as auth from 'services/auth';
import { client } from 'services/api-client';
import { useAsync } from 'hooks/use-async';
import { FullPageSpinner, FullPageErrorFallback } from 'components/lib';
import { getUser } from 'services/user';

const AuthContext = React.createContext();
AuthContext.displayName = 'AuthContext';

const fetchUser = async () => {
  let user = null;

  const token = await auth.getToken();
  if (token) {
    user = await getUser({ token });
  }
  return user;
};

function AuthProvider(props) {
  const {
    data: user,
    status,
    error,
    isLoading,
    isIdle,
    isError,
    isSuccess,
    run,
    setData,
  } = useAsync();

  React.useEffect(() => {
    run(fetchUser());
  }, [run]);

  const login = React.useCallback(
    form => auth.login(form).then(user => setData(user)),
    [setData],
  );
  const register = React.useCallback(
    form => auth.register(form).then(user => setData(user)),
    [setData],
  );
  const logout = React.useCallback(async () => {
    await auth.logout();
    setData(null);
    return Promise.resolve();
  }, [setData]);

  const value = React.useMemo(() => ({ user, login, logout, register }), [
    login,
    logout,
    register,
    user,
  ]);

  if (isLoading || isIdle) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <FullPageErrorFallback error={error} />;
  }

  if (isSuccess) {
    return <AuthContext.Provider value={value} {...props} />;
  }

  throw new Error(`Unhandled status: ${status}`);
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

function useClient() {
  const token = auth.getToken();
  return React.useCallback(
    (endpoint, config) => {
      return token.then(t => {
        return client(endpoint, { ...config, token: t });
      });
    },
    [token],
  );
}

export { AuthProvider, useAuth, useClient };
